import { motion } from "framer-motion";

/**
 * Pure side-view box truck (SVG). Cabin shows the LEFT side profile only —
 * no front-facing grille or twin headlights. Wheels use translate() so the
 * spinning rims stay perfectly inside the tire boundaries during animation.
 */
export default function Truck3D({ driving, onArrived, showLogo, panelRef, departing = false }) {
  return (
    <motion.div
      data-testid="hero-truck-container"
      initial={{ x: "-110vw" }}
      animate={departing ? { x: "120vw" } : { x: 0 }}
      transition={{ duration: departing ? 1.6 : 1.9, ease: [0.22, 1, 0.36, 1] }}
      onAnimationComplete={() => { if (!departing) onArrived?.(); }}
      className="relative"
      style={{ width: 880, maxWidth: "96vw" }}
    >
      <div className={`relative ${driving || departing ? "bounce-truck" : ""}`}>
        {(driving || departing) && (
          <>
            <span className="smoke-puff absolute left-[68%] bottom-[18%] w-7 h-7 rounded-full bg-stone-300/60" />
            <span className="smoke-puff absolute left-[70%] bottom-[24%] w-5 h-5 rounded-full bg-stone-400/60" style={{ animationDelay: "0.5s" }} />
            <span className="smoke-puff absolute left-[67%] bottom-[14%] w-4 h-4 rounded-full bg-stone-500/60" style={{ animationDelay: "0.9s" }} />
          </>
        )}

        <div className="relative" style={{ aspectRatio: "1100 / 500" }}>
          <svg viewBox="0 0 1100 500" className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="boxBody" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FAF7F1" />
                <stop offset="40%" stopColor="#F2EDE2" />
                <stop offset="100%" stopColor="#E5DECF" />
              </linearGradient>
              <linearGradient id="cabinBody" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1B2230" />
                <stop offset="55%" stopColor="#0E1422" />
                <stop offset="100%" stopColor="#070B14" />
              </linearGradient>
              <linearGradient id="windshield" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#A8CCE6" />
                <stop offset="100%" stopColor="#385F80" />
              </linearGradient>
              <linearGradient id="glare" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.65" />
                <stop offset="60%" stopColor="#ffffff" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="chassis" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3A3F4A" />
                <stop offset="100%" stopColor="#181B22" />
              </linearGradient>
              <radialGradient id="rim" cx="0.5" cy="0.5" r="0.5">
                <stop offset="0%" stopColor="#E7E5E1" />
                <stop offset="55%" stopColor="#9A988F" />
                <stop offset="100%" stopColor="#3A3833" />
              </radialGradient>
              <radialGradient id="hubcap" cx="0.5" cy="0.5" r="0.5">
                <stop offset="0%" stopColor="#F5F2EA" />
                <stop offset="100%" stopColor="#7C7A72" />
              </radialGradient>
              <linearGradient id="bumper" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#D6D4CC" />
                <stop offset="100%" stopColor="#7A7770" />
              </linearGradient>
              <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="5" />
              </filter>
            </defs>

            {/* Ground shadow */}
            <ellipse cx="550" cy="470" rx="490" ry="14" fill="#0E1116" opacity="0.22" filter="url(#softShadow)" />

            {/* ============ CARGO BOX ============ */}
            <g>
              <rect x="38" y="34" width="700" height="6" rx="2" fill="#0E1116" opacity="0.25" />
              <rect x="40" y="40" width="700" height="340" rx="6" fill="url(#boxBody)" stroke="#1F242E" strokeWidth="2.5" />
              <rect x="40" y="40" width="700" height="14" fill="#1F242E" />
              <rect x="40" y="362" width="700" height="18" fill="#1F242E" />
              {Array.from({ length: 16 }).map((_, i) => (
                <circle key={`rt-${i}`} cx={70 + i * 42} cy={62} r={2.4} fill="#A39F94" />
              ))}
              {Array.from({ length: 16 }).map((_, i) => (
                <circle key={`rb-${i}`} cx={70 + i * 42} cy={353} r={2.4} fill="#A39F94" />
              ))}
              <line x1="715" y1="54" x2="715" y2="362" stroke="#1F242E" strokeWidth="2" />
              <rect x="705" y="220" width="6" height="44" rx="2" fill="#1F242E" />
              <rect x="44" y="54" width="6" height="308" fill="#ffffff" opacity="0.55" />
              <rect x="725" y="54" width="2" height="308" fill="#0E1116" opacity="0.15" />
              <rect x="50" y="306" width="62" height="22" rx="3" fill="#F5F2EA" stroke="#1F242E" strokeWidth="1.5" />
              <text x="81" y="322" textAnchor="middle" fontSize="13" fontFamily="ui-monospace, Menlo, monospace" fill="#1F242E" fontWeight="700">SH-2025</text>
              <rect x="42" y="248" width="10" height="38" rx="2" fill="#B91C1C" stroke="#1F242E" strokeWidth="1.5" />
            </g>

            {/* ============ CABIN — PURE SIDE PROFILE ============
                 Viewed strictly from the left side of the truck.
                 No front grille, no twin headlights, no front bumper face. */}
            <g>
              {/* Back connection panel between cargo box and cabin */}
              <rect x="740" y="160" width="22" height="220" fill="#070B14" />

              {/* Cabin silhouette: tall back, smooth top, short hood protruding at the front-bottom.
                  No second windshield on the hood. */}
              <path
                d="M 762 160
                   L 762 380
                   L 1058 380
                   L 1058 300
                   L 1044 268
                   Q 1042 256 1030 250
                   L 980 250
                   L 980 175
                   Q 980 170 970 170
                   L 790 170
                   Q 770 172 768 188
                   L 762 200
                   Z"
                fill="url(#cabinBody)"
                stroke="#040711"
                strokeWidth="2.5"
              />

              {/* Roof highlight */}
              <path d="M 790 180 Q 800 173 820 172 L 940 172" stroke="#ffffff" strokeOpacity="0.16" strokeWidth="3" fill="none" />

              {/* SIDE WINDOW (driver door) — the ONLY glass visible on the side view */}
              <rect x="780" y="190" width="170" height="50" rx="3" fill="url(#windshield)" stroke="#040711" strokeWidth="1.5" />
              {/* Window divider (door post) */}
              <line x1="864" y1="190" x2="864" y2="240" stroke="#040711" strokeWidth="1.2" opacity="0.6" />
              {/* Glare on window */}
              <path d="M 786 232 L 826 196 L 818 240 L 786 240 Z" fill="url(#glare)" opacity="0.55" />

              {/* DOOR seam */}
              <line x1="780" y1="242" x2="780" y2="378" stroke="#040711" strokeWidth="1.2" opacity="0.55" />
              <line x1="958" y1="242" x2="958" y2="378" stroke="#040711" strokeWidth="1.2" opacity="0.45" />

              {/* Door handle */}
              <rect x="888" y="270" width="28" height="4" rx="1.5" fill="#C9A66B" />

              {/* SIDE MIRROR — single mirror on a single stalk */}
              <line x1="772" y1="210" x2="748" y2="222" stroke="#040711" strokeWidth="3" />
              <rect x="730" y="214" width="18" height="20" rx="3" fill="#0E1422" stroke="#040711" strokeWidth="1.5" />
              <rect x="733" y="217" width="12" height="14" rx="1.5" fill="#5C7A95" />

              {/* HOOD — short side profile under the front bulkhead */}
              <path
                d="M 980 250 L 1030 250 Q 1042 256 1044 268 L 980 268 Z"
                fill="#141B2C"
                stroke="#040711"
                strokeWidth="1.2"
              />
              {/* Hood seam line */}
              <line x1="982" y1="258" x2="1042" y2="262" stroke="#040711" strokeWidth="0.6" opacity="0.4" />

              {/* FRONT EDGE — side view of front, with subtle headlight strip */}
              <path
                d="M 1044 268 L 1058 296 L 1058 358 L 1040 358 L 1040 280 Z"
                fill="#0B1020"
                stroke="#040711"
                strokeWidth="1.2"
              />
              {/* Slim side-profile headlight (warm amber sliver) */}
              <rect x="1046" y="290" width="10" height="14" rx="1" fill="#FFE9A8" stroke="#040711" strokeWidth="1" />
              {/* Side reflector / amber turn signal */}
              <rect x="1046" y="312" width="9" height="6" rx="1" fill="#F59E0B" stroke="#040711" strokeWidth="0.8" />

              {/* FRONT BUMPER — side profile only (chrome lip protruding at bottom-front) */}
              <path
                d="M 1040 354
                   L 1062 358
                   L 1064 374
                   L 1040 374 Z"
                fill="url(#bumper)"
                stroke="#040711"
                strokeWidth="1.2"
              />
              <line x1="1040" y1="365" x2="1062" y2="367" stroke="#0B0E13" strokeWidth="0.6" opacity="0.35" />

              {/* Lower cab side skirt strip — copper accent */}
              <rect x="762" y="346" width="278" height="3" fill="#C2410C" opacity="0.85" />

              {/* Wheel arch over the front wheel cut into the cabin lower edge */}
              <path
                d="M 936 380 Q 980 336 1024 380 Z"
                fill="#070B14"
              />
            </g>

            {/* ============ CHASSIS ============ */}
            <rect x="40" y="380" width="1024" height="14" fill="url(#chassis)" />
            <rect x="600" y="386" width="62" height="22" rx="6" fill="#5C5F69" stroke="#0E1116" strokeWidth="1.5" />
            <rect x="678" y="392" width="42" height="6" fill="#7C7A72" />
            <circle cx="722" cy="395" r="5" fill="#3A3F4A" stroke="#0E1116" strokeWidth="1" />

            {/* ============ WHEELS — translate() group so spin stays centered ============ */}
            {[140, 500, 980].map((cx) => (
              <g key={cx} transform={`translate(${cx} 410)`}>
                {/* Wheel-well drop shadow */}
                <ellipse cx="0" cy="-12" rx="50" ry="10" fill="#0E1116" opacity="0.45" />
                {/* Tire */}
                <circle cx="0" cy="0" r="46" fill="#0F1216" stroke="#040711" strokeWidth="2" />
                {/* Tread */}
                {Array.from({ length: 18 }).map((_, i) => {
                  const a = (i * 360) / 18;
                  const r1 = 38, r2 = 45;
                  const x1 = r1 * Math.cos((a * Math.PI) / 180);
                  const y1 = r1 * Math.sin((a * Math.PI) / 180);
                  const x2 = r2 * Math.cos((a * Math.PI) / 180);
                  const y2 = r2 * Math.sin((a * Math.PI) / 180);
                  return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#2A2D33" strokeWidth="2" />;
                })}
                {/* Spinning rim — rotates around (0,0) which is the wheel center */}
                <g className={driving || departing ? "spin-wheel" : ""}>
                  <circle cx="0" cy="0" r="34" fill="url(#rim)" />
                  <circle cx="0" cy="0" r="29" fill="#1A1D24" />
                  {Array.from({ length: 5 }).map((_, i) => {
                    const a = (i * 72) - 90;
                    const x = 27 * Math.cos((a * Math.PI) / 180);
                    const y = 27 * Math.sin((a * Math.PI) / 180);
                    return (
                      <line key={i} x1="0" y1="0" x2={x} y2={y} stroke="#D6D2C8" strokeWidth="5" strokeLinecap="round" />
                    );
                  })}
                  <circle cx="0" cy="0" r="9" fill="url(#hubcap)" stroke="#1A1D24" strokeWidth="1.5" />
                  <circle cx="0" cy="0" r="3" fill="#1A1D24" />
                </g>
              </g>
            ))}

            <rect x={96} y={410} width={10} height={36} rx="2" fill="#0E1116" />
          </svg>

          {/* Floating-card anchor */}
          <div
            ref={panelRef}
            data-testid="truck-side-panel"
            className="absolute"
            style={{
              left: "4.5%",
              top: "11.2%",
              width: "60.5%",
              height: "60.4%",
              pointerEvents: "auto",
            }}
          >
            {showLogo && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.6 }}
                className="absolute inset-0 flex items-center justify-center px-6"
              >
                <div className="text-center select-none">
                  <div className="inline-flex items-center gap-2 mb-2">
                    <span className="inline-block w-1.5 h-1.5 bg-amber-700 rounded-full" />
                    <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.32em] font-semibold text-stone-700">
                      Est. 2025 · Licensed & Insured
                    </span>
                    <span className="inline-block w-1.5 h-1.5 bg-amber-700 rounded-full" />
                  </div>
                  <h2 className="font-display font-black text-stone-900 leading-[0.85] text-3xl sm:text-5xl md:text-6xl tracking-[-0.04em]">
                    SWIFT<span className="italic font-light text-amber-700">haul</span>
                  </h2>
                  <div className="mt-3 inline-flex items-center gap-3 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.3em] text-stone-700">
                    <span>Moving</span>
                    <span className="w-1 h-1 bg-amber-700 rounded-full" />
                    <span>Junk Removal</span>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
